import { resolveSectionQuestions } from '@/features/rules/fact-key-palette'
import { validateLogic } from '@/features/rules/logic-validate'
import { CategoryIndex } from '@/lib/category-ref'
import { ANSWER_TYPE_LABELS, COST_TYPE_LABELS, TRANSLATION_LANGUAGES } from '@/lib/constants'
import type {
  ConfigQuestion,
  EstateType,
  Material,
  MaterialAttributeValue,
  MaterialCategory,
  MaterialCategoryAttribute,
  MaterialEffect,
  MaterialPrice,
  QuestionGroup,
  QuestionHint,
  QuestionOption,
  SectionType,
  UnitOfMeasure,
} from '@/lib/types'

/**
 * Data-consistency checks for the admin console ("Несоответствия"). Pure functions over the already
 * loaded collections — no Firestore here. Every rule mirrors how the Android app actually reads the
 * data, so a reported row is something that misbehaves in the app, not a matter of taste:
 *
 *  - a material's category and a question's / field's material category are resolved like the app
 *    does (`CategoryIndex`): by id (`categoryId` / `materialCategoryId`), else — documents saved
 *    before ids existed — by name, exact then ignoring case;
 *  - `{key}` placeholders resolve against the room's facts, i.e. the answers of the questions that
 *    apply to the room (`SectionQuestionResolver`); REPEATER / QUESTION_LIST field keys exist only
 *    inside that question's own effects; `{attr:CODE}` only inside effects whose target is a material
 *    picked in the interview, looked up by the value's stored `attributeCode` (`AutomatedMaterials`);
 *  - expressions are parsed by the same grammar as `LogicEvaluator` (`logic-validate.ts`).
 */

// =============================================================================================
// Model
// =============================================================================================

/** error = the app computes or shows something wrong; warning = likely a mistake; info = polish. */
export type Severity = 'error' | 'warning' | 'info'

/** One row of a check. Every check produces the same shape, so one table renders them all. */
export type Issue = {
  id: string
  /** Short problem type — the filter of the table. */
  kind: string
  /** What is affected: a material name, a question prompt… */
  subject: string
  /** Secondary identifier under the subject: code, fact key. */
  subjectNote: string
  where: string
  detail: string
  problem: string
  /** In-console URL that opens the affected item; `null` when there is nothing to open. */
  link: string | null
  /** A one-click repair the page can run for this row (optional). */
  fix?: IssueFix
}

/** Repairs the page knows how to run. Each is a minimal, targeted write. */
export type IssueFix =
  | {
      kind: 'unlink-section-groups'
      sectionTypeId: string
      groupIds: string[]
      label: string
    }
  | {
      /** Stores the category id (and its current name) on a material, question or repeater field. */
      kind: 'link-category'
      write: CategoryLinkWrite
      label: string
    }
  | {
      /** Renumbers the categories 0, 1, 2… in their current order (only changed ones are written). */
      kind: 'renumber-categories'
      updates: { id: string; orderIndex: number }[]
      label: string
    }

/** One category-reference write: `{[idField]: categoryId, [nameField]: categoryName}` on a doc. */
export type CategoryLinkWrite = {
  collection: 'materials' | 'ruleQuestions' | 'ruleQuestionOptions'
  docId: string
  categoryId: string
  categoryName: string
}

export type CheckId =
  | 'category-links'
  | 'category-order'
  | 'material-category'
  | 'material-unit'
  | 'material-price'
  | 'material-attributes'
  | 'attribute-values'
  | 'duplicates'
  | 'question-category'
  | 'question-empty-choice'
  | 'keys'
  | 'syntax'
  | 'effects'
  | 'links'
  | 'structure'
  | 'translations'

/** The collections a check reads (the page waits for them before showing a count). */
export type Source =
  | 'materials'
  | 'categories'
  | 'attributes'
  | 'attributeValues'
  | 'units'
  | 'prices'
  | 'rules'

export type CheckGroup = 'catalog' | 'rules' | 'translations'

export type CheckDefinition = {
  id: CheckId
  group: CheckGroup
  severity: Severity
  title: string
  description: string
  /** Column headers of the table. */
  headers: { subject: string; where: string; detail: string }
  sources: Source[]
}

export type CheckInput = {
  materials: Material[]
  categories: MaterialCategory[]
  attributes: MaterialCategoryAttribute[]
  attributeValues: MaterialAttributeValue[]
  units: UnitOfMeasure[]
  prices: Map<string, MaterialPrice>
  estateTypes: EstateType[]
  sectionTypes: SectionType[]
  groups: QuestionGroup[]
  hints: QuestionHint[]
  questions: ConfigQuestion[]
  options: QuestionOption[]
  effects: MaterialEffect[]
}

export const CHECK_GROUP_LABELS: Record<CheckGroup, string> = {
  catalog: 'Каталог',
  rules: 'Правила расчёта',
  translations: 'Переводы',
}

const VISIBLE_NOTE =
  ' Проверяются пункты, видные администратору: общий каталог и свои личные — чужие личные пункты правила Firestore прочитать не дают.'

export const CHECKS: CheckDefinition[] = [
  {
    id: 'category-links',
    group: 'catalog',
    severity: 'warning',
    title: 'Связь с категорией только по названию',
    description:
      'Пункты затрат, вопросы и поля «Выбор», которые привязаны к категории только названием (сохранены до появления привязки по id), ссылаются на удалённую категорию, но нашлась категория с тем же названием, или хранят устаревшую копию названия. Новые версии приложения и консоли находят такую категорию по названию, но переименование категории их отвяжет, а старые версии приложения показывают сохранённую копию. «Привязать» записывает id категории и её текущее название; «Исправить все» — для всех строк таблицы. Строки, где id и сохранённое название указывают на разные категории, автоматически не исправляются.' +
      VISIBLE_NOTE,
    headers: { subject: 'Запись', where: 'Где', detail: 'Сохранено → категория' },
    sources: ['materials', 'categories', 'rules'],
  },
  {
    id: 'category-order',
    group: 'catalog',
    severity: 'warning',
    title: 'Порядок категорий',
    description:
      'Порядок категорий не идёт подряд 0, 1, 2…: у нескольких категорий одинаковый номер (тогда их порядок определяет название) или в нумерации пропуски. «Упорядочить» перенумеровывает все категории подряд, сохраняя их нынешний порядок в списках приложения; новые категории дальше встают в конец автоматически.',
    headers: { subject: 'Справочник', where: 'Проблема', detail: 'Изменится' },
    sources: ['categories'],
  },
  {
    id: 'material-category',
    group: 'catalog',
    severity: 'error',
    title: 'Пункты затрат с некорректной категорией',
    description:
      'Категория не указана, удалена или её нет в справочнике (поиск по id, у старых пунктов — по названию, как в приложении). Такой пункт не предлагается ни в одном вопросе «Выбор» и не получает атрибутов. Несовпадение типа затрат пункта и категории ошибкой не считается — так задумано.' +
      VISIBLE_NOTE,
    headers: { subject: 'Пункт', where: 'Тип затрат', detail: 'Категория' },
    sources: ['materials', 'categories'],
  },
  {
    id: 'material-unit',
    group: 'catalog',
    severity: 'error',
    title: 'Пункты затрат с проблемной единицей измерения',
    description:
      'Единица не указана, удалена из справочника или скрыта, либо сохранённый у пункта символ (его показывает смета) уже не совпадает с символом единицы.' +
      VISIBLE_NOTE,
    headers: { subject: 'Пункт', where: 'Тип затрат', detail: 'Единица' },
    sources: ['materials', 'units'],
  },
  {
    id: 'material-price',
    group: 'catalog',
    severity: 'warning',
    title: 'Общие пункты без цены',
    description:
      'Активные пункты общего каталога, у которых нет цены: в смете пользователя их сумма будет нулевой, пока он не задаст цену сам.',
    headers: { subject: 'Пункт', where: 'Тип затрат', detail: 'Категория' },
    sources: ['materials', 'prices'],
  },
  {
    id: 'material-attributes',
    group: 'catalog',
    severity: 'warning',
    title: 'Пункты с незаполненными атрибутами категории',
    description:
      'У категории есть атрибуты, а у пункта нет их значений: формулы с {attr:КОД} получат для него пустое значение (0).' +
      VISIBLE_NOTE,
    headers: { subject: 'Пункт', where: 'Категория', detail: 'Не заполнены' },
    sources: ['materials', 'categories', 'attributes', 'attributeValues'],
  },
  {
    id: 'attribute-values',
    group: 'catalog',
    severity: 'error',
    title: 'Устаревшие значения атрибутов',
    description:
      'Значения, оставшиеся от прежней категории пункта или от удалённого атрибута, и значения, сохранённые под старым кодом атрибута. Движок ищет значение по коду, поэтому {attr:КОД} может взять не то значение или не найти нужное. Исправляется повторным сохранением пункта. Значения чужих личных пунктов не проверяются.',
    headers: { subject: 'Пункт', where: 'Категория', detail: 'Значения' },
    sources: ['materials', 'categories', 'attributes', 'attributeValues'],
  },
  {
    id: 'duplicates',
    group: 'catalog',
    severity: 'warning',
    title: 'Дубли в каталоге',
    description:
      'Одинаковые коды пунктов, одинаковые названия общих пунктов в одной категории, категории с одинаковым названием (старые записи без id и импорт CSV находят категорию по названию — какая из них имеется в виду, не определено) и одинаковые коды атрибутов в категории.',
    headers: { subject: 'Объект', where: 'Где', detail: 'Значение' },
    sources: ['materials', 'categories', 'attributes'],
  },
  {
    id: 'question-category',
    group: 'rules',
    severity: 'error',
    title: 'Вопросы с несуществующей категорией',
    description:
      'Вопросы «Выбор» / «Выбор несколько» и поля «Выбор» повторителя и списка вопросов, у которых категория не указана, удалена или её нет в справочнике: в опросе там нечего выбрать, а эффекты не дают строк сметы.',
    headers: { subject: 'Вопрос', where: 'Где', detail: 'Поле · категория' },
    sources: ['rules', 'categories'],
  },
  {
    id: 'question-empty-choice',
    group: 'rules',
    severity: 'error',
    title: 'Вопросы, в которых нечего выбрать',
    description:
      'Категория вопроса (или поля «Выбор») есть в справочнике, но в ней нет ни одного активного пункта общего каталога: пользователь увидит пустой список.',
    headers: { subject: 'Вопрос', where: 'Где', detail: 'Поле · категория' },
    sources: ['rules', 'categories', 'materials'],
  },
  {
    id: 'keys',
    group: 'rules',
    severity: 'error',
    title: 'Вопросы и подсказки с неверными ключами',
    description:
      'Пустые и недопустимые ключи, один ключ у двух вопросов одного помещения, ссылки {ключ} и {attr:КОД} в условиях показа, эффектах и подсказках, которые в приложении всегда дают пустое значение.',
    headers: { subject: 'Вопрос / подсказка', where: 'Где', detail: 'Место · ключ' },
    sources: ['rules', 'categories', 'attributes'],
  },
  {
    id: 'syntax',
    group: 'rules',
    severity: 'error',
    title: 'Выражения с ошибкой',
    description:
      'Условия показа, эффекты и условия подсказок, которые приложение не может разобрать. Ошибка в условии показа означает «показывать всегда», в эффекте — 0, в подсказке — «не показывать никогда».',
    headers: { subject: 'Вопрос / подсказка', where: 'Где', detail: 'Место' },
    sources: ['rules'],
  },
  {
    id: 'effects',
    group: 'rules',
    severity: 'error',
    title: 'Эффекты с неверной целью',
    description:
      'Эффект с постоянным пунктом, который не выбран, удалён, отключён или личный (другим пользователям недоступен); эффект с пустым выражением; эффект повторителя, указывающий на поле, которого больше нет (тогда берётся первое поле «Выбор»).',
    headers: { subject: 'Вопрос', where: 'Где', detail: 'Эффект' },
    sources: ['rules', 'materials'],
  },
  {
    id: 'links',
    group: 'rules',
    severity: 'error',
    title: 'Битые связи в правилах',
    description:
      'Вопросы удалённого раздела или группы, разделы удалённого типа объекта, подключение удалённой группы, варианты, эффекты и подсказки без своего вопроса или группы. Такие записи не видны в дереве правил; запись без владельца можно удалить только в консоли Firebase.',
    headers: { subject: 'Запись', where: 'Где', detail: 'Ссылка' },
    sources: ['rules'],
  },
  {
    id: 'structure',
    group: 'rules',
    severity: 'warning',
    title: 'Неполная структура правил',
    description:
      '«Выбор из списка» и «Набор ключей» без вариантов, повторитель и список вопросов без полей, факты без эффектов, пустые и неподключённые группы, разделы без вопросов, типы объектов без разделов, подсказки без условия.',
    headers: { subject: 'Запись', where: 'Где', detail: 'Подробности' },
    sources: ['rules'],
  },
  {
    id: 'translations',
    group: 'translations',
    severity: 'info',
    title: 'Нет переводов',
    description: `Записи без перевода на ${TRANSLATION_LANGUAGES.map((language) => language.label).join(' / ')}: в приложении на этом языке покажется русский текст. Из пунктов затрат проверяется только общий каталог.`,
    headers: { subject: 'Запись', where: 'Вид', detail: 'Нет перевода' },
    sources: ['rules', 'materials', 'categories', 'units'],
  },
]

// =============================================================================================
// Shared helpers
// =============================================================================================

/** An identifier usable inside `{…}`: the `IDENT` token of the app's `LogicEvaluator`. */
const IDENT = /^[\p{L}_][\p{L}\p{Nd}_]*$/u

export function isValidKey(key: string): boolean {
  return IDENT.test(key)
}

const SCHEMA_TYPES = new Set(['REPEATER', 'QUESTION_LIST'])
const CATEGORY_TYPES = new Set(['CHOICE', 'MULTI_CHOICE'])

/** A placeholder found in an expression: `{key}` (namespace '') or `{ns:name}`. */
export type Placeholder = { namespace: string; name: string; raw: string }

/**
 * The placeholders of an expression, in order. String literals are skipped (the app's tokenizer
 * ends a literal at the next identical quote, no escapes), smart quotes are normalized as in the app.
 */
export function extractPlaceholders(expression: string): Placeholder[] {
  const normalized = expression
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/"[^"]*"|'[^']*'/g, '""')
  const pattern =
    /\{\s*([\p{L}_][\p{L}\p{Nd}_]*)\s*(?::\s*([\p{L}_][\p{L}\p{Nd}_]*)\s*)?(?:\.\s*[\p{L}_][\p{L}\p{Nd}_]*\s*)*\}/gu
  const result: Placeholder[] = []
  for (const match of normalized.matchAll(pattern)) {
    const raw = match[0]
    const first = match[1] ?? ''
    const second = match[2]
    result.push(
      second === undefined
        ? { namespace: '', name: first, raw }
        : { namespace: first, name: second, raw },
    )
  }
  return result
}

function groupBy<T>(items: readonly T[], keyOf: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>()
  for (const item of items) {
    const key = keyOf(item)
    const list = map.get(key)
    if (list) list.push(item)
    else map.set(key, [item])
  }
  return map
}

/** Options grouped by question, each list in `orderIndex` order (how the app reads them). */
export function optionsByQuestion(options: QuestionOption[]): Map<string, QuestionOption[]> {
  const map = groupBy(options, (option) => option.questionId)
  for (const list of map.values()) list.sort((left, right) => left.orderIndex - right.orderIndex)
  return map
}

/** The keys a question writes into the room's facts (its factKey, or a KEY_SET's item keys). */
function roomKeysOf(question: ConfigQuestion, options: QuestionOption[]): string[] {
  if (question.answerType === 'KEY_SET') return options.map((option) => option.value.trim())
  // FACT stores no answer, so it writes no key.
  if (question.answerType === 'FACT') return []
  return [question.factKey.trim()]
}


const normalizeName = (value: string) => value.trim().toLowerCase()

// ---- Links into the console (the target pages open the item from these query parameters) ----

export const links = {
  material: (id: string) => `/materials?edit=${encodeURIComponent(id)}`,
  category: (id: string) => `/categories?edit=${encodeURIComponent(id)}`,
  unit: (id: string) => `/units?edit=${encodeURIComponent(id)}`,
  question: (id: string) => `/rules?question=${encodeURIComponent(id)}`,
  hint: (id: string) => `/rules?hint=${encodeURIComponent(id)}`,
  section: (id: string) => `/rules?section=${encodeURIComponent(id)}`,
  group: (id: string) => `/rules?group=${encodeURIComponent(id)}`,
  estate: (id: string) => `/rules?estate=${encodeURIComponent(id)}`,
}

/** Lookups built once per run. */
type Context = CheckInput & {
  optionsOf: Map<string, QuestionOption[]>
  effectsOf: Map<string, MaterialEffect[]>
  /** Resolves category references like the app (id, else name). */
  categoryIndex: CategoryIndex
  sectionById: Map<string, SectionType>
  groupById: Map<string, QuestionGroup>
  estateById: Map<string, EstateType>
  questionById: Map<string, ConfigQuestion>
  materialById: Map<string, Material>
}

function buildContext(input: CheckInput): Context {
  return {
    ...input,
    optionsOf: optionsByQuestion(input.options),
    effectsOf: groupBy(input.effects, (effect) => effect.questionId),
    categoryIndex: new CategoryIndex(input.categories),
    sectionById: new Map(input.sectionTypes.map((item) => [item.id, item])),
    groupById: new Map(input.groups.map((item) => [item.id, item])),
    estateById: new Map(input.estateTypes.map((item) => [item.id, item])),
    questionById: new Map(input.questions.map((item) => [item.id, item])),
    materialById: new Map(input.materials.map((item) => [item.id, item])),
  }
}

/** Where a question lives: "Квартира › Комната", "Группа «Стены»", or a broken link. */
function ownerLabel(question: ConfigQuestion, ctx: Context): string {
  if (question.groupId) {
    const group = ctx.groupById.get(question.groupId)
    return group ? `Группа «${group.name}»` : 'Группа удалена'
  }
  const section = ctx.sectionById.get(question.sectionTypeId)
  if (!section) return question.sectionTypeId ? 'Раздел удалён' : 'Нет раздела и группы'
  const estate = ctx.estateById.get(section.estateTypeId)
  return `${estate?.name ?? 'Тип объекта удалён'} › ${section.name}`
}

/** A question can be opened only when its owner (section with its estate type, or group) exists. */
function questionLink(question: ConfigQuestion, ctx: Context): string | null {
  if (question.groupId) return ctx.groupById.has(question.groupId) ? links.question(question.id) : null
  const section = ctx.sectionById.get(question.sectionTypeId)
  return section && ctx.estateById.has(section.estateTypeId) ? links.question(question.id) : null
}

function sectionLink(section: SectionType, ctx: Context): string | null {
  return ctx.estateById.has(section.estateTypeId) ? links.section(section.id) : null
}

function hintLink(hint: QuestionHint, ctx: Context): string | null {
  return ctx.groupById.has(hint.groupId) ? links.hint(hint.id) : null
}

/** Builder that numbers rows. */
function collector() {
  const issues: Issue[] = []
  return {
    issues,
    add: (issue: Omit<Issue, 'id' | 'subjectNote' | 'where' | 'detail'> & Partial<Issue>) =>
      issues.push({ subjectNote: '', where: '', detail: '', ...issue, id: `${issues.length}` }),
  }
}

/** Subject/where/link of a question row. */
function questionRow(question: ConfigQuestion, ctx: Context) {
  return {
    subject: question.prompt || '—',
    subjectNote: question.factKey,
    where: ownerLabel(question, ctx),
    link: questionLink(question, ctx),
  }
}

function materialRow(material: Material) {
  return {
    subject: material.name || '—',
    subjectNote: material.code,
    link: links.material(material.id),
  }
}

/** Which material an effect adds — the same resolution as the engine (`AutomatedMaterials.kt`). */
type EffectTarget =
  | { kind: 'picked'; categoryId: string; category: string }
  | { kind: 'fixed' }
  | { kind: 'picked-field'; field: QuestionOption; staleKey: boolean }

function effectTarget(question: ConfigQuestion, effect: MaterialEffect, ctx: Context): EffectTarget {
  if (CATEGORY_TYPES.has(question.answerType)) {
    return { kind: 'picked', categoryId: question.materialCategoryId, category: question.materialCategory }
  }
  if (SCHEMA_TYPES.has(question.answerType)) {
    // A CHOICE field without a key is not a material field (as in the engine).
    const fields = (ctx.optionsOf.get(question.id) ?? []).filter(
      (option) => option.fieldType === 'CHOICE' && option.value.trim() !== '',
    )
    if (fields.length > 0) {
      const key = effect.targetFieldKey.trim()
      const named = key ? fields.find((field) => field.value === effect.targetFieldKey) : undefined
      const field = named ?? fields[0]
      if (!field) return { kind: 'fixed' }
      return { kind: 'picked-field', field, staleKey: key.length > 0 && !named }
    }
  }
  return { kind: 'fixed' }
}

// =============================================================================================
// Checks
// =============================================================================================

export function runCheck(id: CheckId, input: CheckInput): Issue[] {
  const ctx = buildContext(input)
  return RUNNERS[id](ctx)
}

/** Every check over one shared set of lookups. */
export function runAllChecks(input: CheckInput): Record<CheckId, Issue[]> {
  const ctx = buildContext(input)
  const result = {} as Record<CheckId, Issue[]>
  for (const check of CHECKS) result[check.id] = RUNNERS[check.id](ctx)
  return result
}

const RUNNERS: Record<CheckId, (ctx: Context) => Issue[]> = {
  'category-links': checkCategoryLinks,
  'category-order': checkCategoryOrder,
  'material-category': checkMaterialCategory,
  'material-unit': checkMaterialUnit,
  'material-price': checkMaterialPrice,
  'material-attributes': checkMaterialAttributes,
  'attribute-values': checkAttributeValues,
  duplicates: checkDuplicates,
  'question-category': checkQuestionCategory,
  'question-empty-choice': checkQuestionEmptyChoice,
  keys: checkKeys,
  syntax: checkSyntax,
  effects: checkEffects,
  links: checkLinks,
  structure: checkStructure,
  translations: checkTranslations,
}

const byName = (left: Material, right: Material) => left.name.localeCompare(right.name, 'ru')

// ---- Catalog ---------------------------------------------------------------------------------

/** Where a category reference is stored, for the link check and its fix. */
type CategoryRefRow = {
  base: Omit<Issue, 'id' | 'kind' | 'problem' | 'detail'>
  what: string
  collection: CategoryLinkWrite['collection']
  docId: string
  categoryId: string
  categoryName: string
}

function checkCategoryLinks(ctx: Context): Issue[] {
  const { issues, add } = collector()
  const rows: CategoryRefRow[] = []
  for (const material of [...ctx.materials].sort(byName)) {
    rows.push({
      base: { ...materialRow(material), where: COST_TYPE_LABELS[material.costType] },
      what: 'Пункт',
      collection: 'materials',
      docId: material.id,
      categoryId: material.categoryId,
      categoryName: material.category,
    })
  }
  for (const { question, field } of categoryUses(ctx)) {
    rows.push({
      base: questionRow(question, ctx),
      what: field ? `Поле «${field.label || field.value}»` : 'Вопрос',
      collection: field ? 'ruleQuestionOptions' : 'ruleQuestions',
      docId: field ? field.id : question.id,
      categoryId: field ? field.materialCategoryId : question.materialCategoryId,
      categoryName: field ? field.materialCategory : question.materialCategory,
    })
  }
  for (const row of rows) {
    const category = ctx.categoryIndex.find(row.categoryId, row.categoryName)
    // Unresolvable references are the category checks' business.
    if (!category) continue
    const storedId = row.categoryId.trim()
    // The stored name copy names ANOTHER existing category than the id: an old app build changed the
    // category (it writes the name only, the old id stays and wins). Which one is meant is unknown,
    // so this row gets no automatic fix — open the item and pick the category again.
    const byStoredName = storedId !== '' ? ctx.categoryIndex.find('', row.categoryName) : null
    if (byStoredName && byStoredName.id !== category.id) {
      add({
        ...row.base,
        detail: `${row.collection === 'materials' ? '' : `${row.what} · `}id → «${category.name}», название → «${byStoredName.name}»`,
        kind: 'id и название указывают на разные категории',
        problem: `Привязка по id — «${category.name}», а сохранённое название — «${byStoredName.name}» (так бывает, если категорию сменили в старой версии приложения). Сейчас действует «${category.name}»; откройте запись и выберите нужную категорию заново`,
      })
      continue
    }
    const kind =
      storedId === ''
        ? `${row.what === 'Пункт' ? 'Пункт' : row.collection === 'ruleQuestions' ? 'Вопрос' : 'Поле'}: только по названию`
        : storedId !== category.id
          ? 'Ссылка на удалённую категорию'
          : row.categoryName !== category.name
            ? 'Устаревшая копия названия'
            : null
    if (kind === null) continue
    add({
      ...row.base,
      detail: `${row.collection === 'materials' ? '' : `${row.what} · `}«${row.categoryName || '—'}» → «${category.name}»`,
      kind,
      problem:
        storedId === ''
          ? 'Привязан к категории только названием: переименование категории его отвяжет'
          : storedId !== category.id
            ? 'Сохранённой категории нет; найдена категория с тем же названием — привязать к ней'
            : 'Привязка по id есть, но сохранена старая копия названия (её показывают старые версии приложения)',
      fix: {
        kind: 'link-category',
        label: storedId === '' || storedId !== category.id ? 'Привязать' : 'Обновить',
        write: {
          collection: row.collection,
          docId: row.docId,
          categoryId: category.id,
          categoryName: category.name,
        },
      },
    })
  }
  return issues
}

/**
 * The categories in the order the app lists them (`sortedForDisplay`: `orderIndex`, then lower-cased
 * name — compared by UTF-16 code units, like Kotlin's `compareTo`), then by id so the result is stable.
 */
export function categoriesInDisplayOrder(categories: readonly MaterialCategory[]): MaterialCategory[] {
  const compare = (left: string, right: string) => (left < right ? -1 : left > right ? 1 : 0)
  return [...categories].sort(
    (left, right) =>
      left.orderIndex - right.orderIndex ||
      compare(left.name.toLowerCase(), right.name.toLowerCase()) ||
      compare(left.id, right.id),
  )
}

function checkCategoryOrder(ctx: Context): Issue[] {
  const { issues, add } = collector()
  const ordered = categoriesInDisplayOrder(ctx.categories)
  const updates = ordered
    .map((category, index) => ({ category, index }))
    .filter(({ category, index }) => category.orderIndex !== index)
  if (updates.length === 0) return issues
  const counts = new Map<number, number>()
  for (const category of ordered) counts.set(category.orderIndex, (counts.get(category.orderIndex) ?? 0) + 1)
  const duplicated = [...counts.values()].filter((count) => count > 1).reduce((sum, count) => sum + count, 0)
  // Gaps / wrong start: the distinct numbers are not exactly 0…(distinct − 1).
  const distinct = [...counts.keys()].sort((left, right) => left - right)
  const hasGaps = distinct.some((value, index) => value !== index)
  const problems = [
    duplicated > 0 ? `одинаковый номер у ${duplicated} категор${duplicated === 1 ? 'ии' : 'ий'}` : null,
    hasGaps ? 'пропуски в нумерации' : null,
  ].filter((item): item is string => item !== null)
  const preview = updates
    .slice(0, 5)
    .map(({ category, index }) => `«${category.name}» ${category.orderIndex}→${index}`)
    .join(', ')
  add({
    subject: `Категории (${ordered.length})`,
    where: problems.join('; '),
    detail: `${updates.length} из ${ordered.length}: ${preview}${updates.length > 5 ? '…' : ''}`,
    kind: duplicated > 0 ? 'Повторяющиеся номера' : 'Пропуски в нумерации',
    problem: 'Нумерация не подряд. «Упорядочить» перенумерует категории 0…n−1 без изменения их порядка в списках',
    link: '/categories',
    fix: {
      kind: 'renumber-categories',
      label: 'Упорядочить',
      updates: updates.map(({ category, index }) => ({ id: category.id, orderIndex: index })),
    },
  })
  return issues
}

function checkMaterialCategory(ctx: Context): Issue[] {
  const { issues, add } = collector()
  for (const material of [...ctx.materials].sort(byName)) {
    const base = {
      ...materialRow(material),
      where: COST_TYPE_LABELS[material.costType],
      detail: material.category ? `«${material.category}»` : '—',
    }
    const hasId = material.categoryId.trim().length > 0
    if (!hasId && material.category.trim().length === 0) {
      add({ ...base, kind: 'Категория не указана', problem: 'Категория не указана' })
    } else if (!ctx.categoryIndex.find(material.categoryId, material.category)) {
      add({
        ...base,
        kind: hasId ? 'Категория удалена' : 'Нет в справочнике',
        problem: hasId ? 'Связанной категории больше нет в справочнике' : 'Категории нет в справочнике',
      })
    }
  }
  return issues
}

function checkMaterialUnit(ctx: Context): Issue[] {
  const { issues, add } = collector()
  const unitById = new Map(ctx.units.map((unit) => [unit.id, unit]))
  for (const material of [...ctx.materials].sort(byName)) {
    const base = { ...materialRow(material), where: COST_TYPE_LABELS[material.costType] }
    if (!material.unitId) {
      add({
        ...base,
        detail: material.unit ? `«${material.unit}»` : '—',
        kind: material.unit ? 'Единица не из справочника' : 'Единица не указана',
        problem: material.unit
          ? 'Сохранён только символ, связи со справочником единиц нет'
          : 'Единица не указана: в смете количество будет без единицы',
      })
      continue
    }
    const unit = unitById.get(material.unitId)
    if (!unit) {
      add({
        ...base,
        detail: material.unit ? `«${material.unit}»` : '—',
        kind: 'Единица удалена',
        problem: 'Единицы больше нет в справочнике',
      })
    } else if (!unit.isActive) {
      add({
        ...base,
        detail: `«${unit.symbol}»`,
        kind: 'Единица скрыта',
        problem: `Единица «${unit.name}» скрыта: при правке пункта её нельзя будет выбрать снова`,
      })
    } else if (material.unit !== unit.symbol) {
      add({
        ...base,
        detail: `«${material.unit || '—'}» → «${unit.symbol}»`,
        kind: 'Символ устарел',
        problem: `У пункта сохранён символ «${material.unit || '—'}», в справочнике — «${unit.symbol}». Смета показывает сохранённый символ; исправляется повторным сохранением пункта`,
      })
    }
  }
  return issues
}

function checkMaterialPrice(ctx: Context): Issue[] {
  const { issues, add } = collector()
  for (const material of [...ctx.materials].sort(byName)) {
    if (material.isPrivate || !material.isActive || ctx.prices.has(material.id)) continue
    add({
      ...materialRow(material),
      where: COST_TYPE_LABELS[material.costType],
      detail: shownCategory(material, ctx),
      kind: 'Нет цены',
      problem: 'У общего пункта нет цены',
    })
  }
  return issues
}

/** Values of each material, keyed by material id. */
function valuesByMaterial(ctx: Context): Map<string, MaterialAttributeValue[]> {
  return groupBy(ctx.attributeValues, (value) => value.materialId)
}

function checkMaterialAttributes(ctx: Context): Issue[] {
  const { issues, add } = collector()
  const values = valuesByMaterial(ctx)
  const attributesOf = groupBy(ctx.attributes, (attribute) => attribute.categoryId)
  for (const material of [...ctx.materials].sort(byName)) {
    const category = ctx.categoryIndex.find(material.categoryId, material.category)
    if (!category) continue
    const required = attributesOf.get(category.id) ?? []
    if (required.length === 0) continue
    const own = values.get(material.id) ?? []
    const missing = required.filter(
      (attribute) =>
        !own.some((value) => value.attributeId === attribute.id && value.value.trim().length > 0),
    )
    if (missing.length === 0) continue
    add({
      ...materialRow(material),
      where: `«${category.name}»`,
      detail: missing.map((attribute) => `${attribute.name} (${attribute.code})`).join(', '),
      kind: missing.length === required.length ? 'Не заполнено ни одного' : 'Заполнены не все',
      problem: `Не заполнено ${missing.length} из ${required.length}`,
    })
  }
  return issues
}

function checkAttributeValues(ctx: Context): Issue[] {
  const { issues, add } = collector()
  const attributeById = new Map(ctx.attributes.map((attribute) => [attribute.id, attribute]))
  const values = valuesByMaterial(ctx)
  for (const material of [...ctx.materials].sort(byName)) {
    const own = values.get(material.id) ?? []
    if (own.length === 0) continue
    const category = ctx.categoryIndex.find(material.categoryId, material.category)
    const buckets: Record<string, MaterialAttributeValue[]> = {}
    const put = (kind: string, value: MaterialAttributeValue) => {
      ;(buckets[kind] ??= []).push(value)
    }
    for (const value of own) {
      const attribute = attributeById.get(value.attributeId)
      if (!category) put('Категории пункта нет', value)
      else if (value.categoryId !== category.id) put('От прежней категории', value)
      else if (!attribute) put('Атрибут удалён', value)
      else if (attribute.code !== value.attributeCode) put('Код атрибута изменён', value)
    }
    const problems: Record<string, string> = {
      'Категории пункта нет': 'Значения хранятся, но категории пункта нет в справочнике',
      'От прежней категории': 'Значения от другой категории — пункт сменил категорию',
      'Атрибут удалён': 'Значения атрибутов, которых у категории больше нет',
      'Код атрибута изменён': 'Значение сохранено под старым кодом: {attr:НОВЫЙ_КОД} его не найдёт',
    }
    for (const [kind, list] of Object.entries(buckets)) {
      add({
        ...materialRow(material),
        where: shownCategory(material, ctx),
        detail: list.map((value) => `${value.attributeCode || value.attributeName}=${value.value}`).join(', '),
        kind,
        problem: `${problems[kind]} (${list.length})`,
      })
    }
  }
  return issues
}

function checkDuplicates(ctx: Context): Issue[] {
  const { issues, add } = collector()
  // Material codes (every visible item: codes are meant to be unique).
  for (const [code, list] of groupBy(
    ctx.materials.filter((material) => material.code.trim()),
    (material) => material.code.trim().toLowerCase(),
  )) {
    if (list.length < 2) continue
    for (const material of list) {
      add({
        ...materialRow(material),
        where: COST_TYPE_LABELS[material.costType],
        detail: code,
        kind: 'Одинаковый код пункта',
        problem: `Такой же код у ${list.length - 1} других: ${list.filter((item) => item !== material).map((item) => `«${item.name}»`).join(', ')}`,
      })
    }
  }
  // Shared items with the same name in one category.
  for (const list of groupBy(
    ctx.materials.filter((material) => !material.isPrivate && material.name.trim()),
    (material) =>
      `${ctx.categoryIndex.key(material.categoryId, material.category)}|${normalizeName(material.name)}`,
  ).values()) {
    if (list.length < 2) continue
    for (const material of list) {
      const shown = ctx.categoryIndex.displayName(material.categoryId, material.category)
      add({
        ...materialRow(material),
        where: shown ? `«${shown}»` : 'Без категории',
        detail: material.name,
        kind: 'Одинаковое название в категории',
        problem: `В общем каталоге ${list.length} пункта с таким названием в одной категории`,
      })
    }
  }
  // Category names (references are by name).
  for (const list of groupBy(ctx.categories, (category) => normalizeName(category.name)).values()) {
    if (list.length < 2) continue
    for (const category of list) {
      add({
        subject: category.name,
        subjectNote: '',
        where: COST_TYPE_LABELS[category.costType],
        detail: category.name,
        kind: 'Одинаковое название категории',
        problem: `Ещё ${list.length - 1} категори${list.length - 1 === 1 ? 'я' : 'и'} с таким названием: старые записи без id и импорт CSV находят категорию по названию`,
        link: links.category(category.id),
      })
    }
  }
  // Attribute codes inside one category.
  for (const list of groupBy(
    ctx.attributes.filter((attribute) => attribute.code.trim()),
    (attribute) => `${attribute.categoryId}|${attribute.code.trim().toLowerCase()}`,
  ).values()) {
    if (list.length < 2) continue
    const category = ctx.categories.find((item) => item.id === list[0]?.categoryId)
    for (const attribute of list) {
      add({
        subject: attribute.name,
        subjectNote: attribute.code,
        where: category ? `Категория «${category.name}»` : 'Категория удалена',
        detail: attribute.code,
        kind: 'Одинаковый код атрибута',
        problem: 'Два атрибута одной категории с одним кодом: {attr:КОД} неоднозначен',
        link: category ? links.category(category.id) : null,
      })
    }
  }
  return issues
}

// ---- Rules: categories -----------------------------------------------------------------------

/** Every (question, field) pair that picks from a category. */
function categoryUses(ctx: Context) {
  const uses: {
    question: ConfigQuestion
    field: QuestionOption | null
    categoryId: string
    category: string
  }[] = []
  for (const question of ctx.questions) {
    if (CATEGORY_TYPES.has(question.answerType)) {
      uses.push({
        question,
        field: null,
        categoryId: question.materialCategoryId,
        category: question.materialCategory,
      })
    } else if (SCHEMA_TYPES.has(question.answerType)) {
      for (const field of ctx.optionsOf.get(question.id) ?? []) {
        if (field.fieldType === 'CHOICE') {
          uses.push({
            question,
            field,
            categoryId: field.materialCategoryId,
            category: field.materialCategory,
          })
        }
      }
    }
  }
  return uses
}

/** «Category» of a material under its current name, `—` without one. */
function shownCategory(material: Material, ctx: Context): string {
  const shown = ctx.categoryIndex.displayName(material.categoryId, material.category)
  return shown ? `«${shown}»` : '—'
}

const fieldDetail = (field: QuestionOption | null, category: string) =>
  `${field ? `Поле «${field.label || field.value}» · ` : ''}${category ? `«${category}»` : '—'}`

function checkQuestionCategory(ctx: Context): Issue[] {
  const { issues, add } = collector()
  for (const { question, field, categoryId, category } of categoryUses(ctx)) {
    const base = { ...questionRow(question, ctx), detail: fieldDetail(field, category) }
    const hasId = categoryId.trim().length > 0
    if (!hasId && category.trim().length === 0) {
      add({ ...base, kind: 'Категория не указана', problem: 'Категория не указана' })
    } else if (!ctx.categoryIndex.find(categoryId, category)) {
      add({
        ...base,
        kind: hasId ? 'Категория удалена' : 'Нет в справочнике',
        problem: hasId ? 'Связанной категории больше нет в справочнике' : 'Категории нет в справочнике',
      })
    }
  }
  return issues
}

function checkQuestionEmptyChoice(ctx: Context): Issue[] {
  const { issues, add } = collector()
  // Counted per category reference, the way the interview groups the catalog (`CategoryIndex`).
  const sharedActive = new Map<string, number>()
  const anyVisible = new Map<string, number>()
  for (const material of ctx.materials) {
    const key = ctx.categoryIndex.key(material.categoryId, material.category)
    if (!key) continue
    anyVisible.set(key, (anyVisible.get(key) ?? 0) + 1)
    if (!material.isPrivate && material.isActive) {
      sharedActive.set(key, (sharedActive.get(key) ?? 0) + 1)
    }
  }
  for (const { question, field, categoryId, category } of categoryUses(ctx)) {
    const resolved = ctx.categoryIndex.find(categoryId, category)
    if (!resolved) continue
    const key = ctx.categoryIndex.key(categoryId, category)
    if ((sharedActive.get(key) ?? 0) > 0) continue
    const others = anyVisible.get(key) ?? 0
    add({
      ...questionRow(question, ctx),
      detail: fieldDetail(field, resolved.name),
      kind: others > 0 ? 'Только личные или отключённые пункты' : 'Категория пустая',
      problem:
        others > 0
          ? `В категории ${others} пункт(ов), но ни одного активного общего: пользователи не увидят вариантов`
          : 'В категории нет ни одного пункта',
    })
  }
  return issues
}

// ---- Rules: keys -----------------------------------------------------------------------------

function checkKeys(ctx: Context): Issue[] {
  const { issues, add } = collector()
  const { questions, groups, sectionTypes, attributes, hints } = ctx

  // --- The question's own keys -----------------------------------------------------------------
  for (const question of questions) {
    const row = questionRow(question, ctx)
    const options = ctx.optionsOf.get(question.id) ?? []
    // KEY_SET keys live on its items; FACT has no key at all (it stores no answer).
    if (question.answerType !== 'KEY_SET' && question.answerType !== 'FACT') {
      const key = question.factKey.trim()
      if (key.length === 0) {
        add({ ...row, detail: 'Ключ факта', kind: 'Пустой ключ', problem: 'Ключ факта пустой: ответ некуда сохранить' })
      } else if (!isValidKey(key)) {
        add({
          ...row,
          detail: `Ключ факта · ${key}`,
          kind: 'Недопустимый ключ',
          problem: 'Ключ нельзя использовать в формулах: только буквы, цифры и _, не с цифры',
        })
      }
    }
    if (question.answerType === 'KEY_SET' || SCHEMA_TYPES.has(question.answerType)) {
      const what = question.answerType === 'KEY_SET' ? 'Элемент набора' : 'Поле'
      const seen = new Set<string>()
      for (const option of options) {
        const key = option.value.trim()
        const detail = `${what} «${option.label || key || '—'}»${key ? ` · ${key}` : ''}`
        if (key.length === 0) {
          add({ ...row, detail, kind: 'Пустой ключ', problem: 'Ключ пустой' })
        } else if (!isValidKey(key)) {
          add({
            ...row,
            detail,
            kind: 'Недопустимый ключ',
            problem: 'Ключ нельзя использовать в формулах: только буквы, цифры и _, не с цифры',
          })
        } else if (seen.has(key)) {
          add({ ...row, detail, kind: 'Ключ повторяется', problem: 'Ключ повторяется внутри этого вопроса' })
        }
        seen.add(key)
      }
    }
  }

  // --- Two questions of the same room write the same key ---------------------------------------
  const reportedPairs = new Set<string>()
  for (const section of sectionTypes) {
    const scoped = resolveSectionQuestions(section, section.id, questions, groups)
    const writer = new Map<string, ConfigQuestion>()
    for (const question of scoped) {
      for (const key of new Set(roomKeysOf(question, ctx.optionsOf.get(question.id) ?? []))) {
        if (key.length === 0) continue
        const first = writer.get(key)
        if (!first) {
          writer.set(key, question)
          continue
        }
        if (first.id === question.id) continue
        const pair = [first.id, question.id].sort().join('|') + `|${key}`
        if (reportedPairs.has(pair)) continue
        reportedPairs.add(pair)
        add({
          ...questionRow(question, ctx),
          detail: `Ключ факта · ${key}`,
          kind: 'Один ключ у двух вопросов',
          problem: `Этот же ключ пишет вопрос «${first.prompt || first.factKey}» в тех же помещениях (раздел «${section.name}»): ответы затирают друг друга`,
        })
      }
    }
  }

  // --- References in expressions ---------------------------------------------------------------
  const allRoomKeys = new Set<string>()
  const fieldKeysAnywhere = new Set<string>()
  for (const question of questions) {
    const options = ctx.optionsOf.get(question.id) ?? []
    for (const key of roomKeysOf(question, options)) if (key) allRoomKeys.add(key)
    if (SCHEMA_TYPES.has(question.answerType)) {
      for (const option of options) if (option.value.trim()) fieldKeysAnywhere.add(option.value.trim())
    }
  }
  const sectionScopeKeys = new Map<string, Set<string>>()
  const scopeKeysOf = (question: ConfigQuestion): Set<string> | null => {
    // Only a SECTION question has one definite set of rooms; a group question runs in every room
    // its group applies to, so it is only checked against the whole configuration.
    if (question.groupId || !question.sectionTypeId) return null
    const cached = sectionScopeKeys.get(question.sectionTypeId)
    if (cached) return cached
    const section = ctx.sectionById.get(question.sectionTypeId) ?? null
    const keys = new Set<string>()
    for (const scoped of resolveSectionQuestions(section, question.sectionTypeId, questions, groups)) {
      for (const key of roomKeysOf(scoped, ctx.optionsOf.get(scoped.id) ?? [])) if (key) keys.add(key)
    }
    sectionScopeKeys.set(question.sectionTypeId, keys)
    return keys
  }
  /** The category `{attr:…}` reads from: a reference (id, else name), resolved like the app. */
  type CategoryRef = { id: string; name: string }

  type Owner = { question: ConfigQuestion | null; base: Omit<Issue, 'id' | 'kind' | 'problem' | 'detail'>; place: string }

  /**
   * Checks one expression. `ownFieldKeys` = the repeater fields readable here (own effects only);
   * `attrCategory` = the category whose attributes `{attr:…}` reads here, `null` = not available.
   */
  const checkExpression = (
    expression: string,
    owner: Owner,
    ownFieldKeys: Set<string>,
    attrCategory: CategoryRef | null,
  ) => {
    const seen = new Set<string>()
    const report = (key: string, kind: string, problem: string) =>
      add({ ...owner.base, detail: `${owner.place} · ${key}`, kind, problem })
    for (const placeholder of extractPlaceholders(expression)) {
      const token = placeholder.raw.replace(/\s+/g, '')
      if (seen.has(token)) continue
      seen.add(token)
      const key = placeholder.namespace ? `${placeholder.namespace}:${placeholder.name}` : placeholder.name
      if (placeholder.namespace === 'attr') {
        if (attrCategory === null) {
          report(
            key,
            'Атрибут недоступен',
            'Атрибут здесь недоступен (только в эффекте с материалом, выбранным в опросе): значение всегда пустое',
          )
          continue
        }
        const category = ctx.categoryIndex.find(attrCategory.id, attrCategory.name)
        // A missing category is reported by the category check; nothing to compare with here.
        if (!category) continue
        const codes = attributes
          .filter((attribute) => attribute.categoryId === category.id)
          .map((attribute) => attribute.code)
        if (codes.includes(placeholder.name)) continue
        const sameIgnoringCase = codes.find(
          (code) => code.toLowerCase() === placeholder.name.toLowerCase(),
        )
        report(
          key,
          'Нет такого атрибута',
          sameIgnoringCase
            ? `У категории «${category.name}» атрибут называется ${sameIgnoringCase} (регистр отличается)`
            : `У категории «${category.name}» нет атрибута с кодом ${placeholder.name}`,
        )
        continue
      }
      if (placeholder.namespace) {
        report(key, 'Неизвестный вид ссылки', `Неизвестный вид ссылки «${placeholder.namespace}:» — поддерживается только attr:`)
        continue
      }
      if (ownFieldKeys.has(key)) continue
      if (allRoomKeys.has(key)) {
        const scope = owner.question ? scopeKeysOf(owner.question) : null
        if (scope && !scope.has(key)) {
          report(
            key,
            'Ключ из другого раздела',
            'Ключ есть, но не у вопросов этого раздела (и его групп): в помещении значение всегда пустое',
          )
        }
        continue
      }
      if (fieldKeysAnywhere.has(key)) {
        report(
          key,
          'Поле повторителя вне его эффектов',
          'Это ключ поля повторителя / списка вопросов: вне эффектов того вопроса значение всегда пустое',
        )
      } else {
        report(key, 'Ключ не найден', 'Такой ключ не пишет ни один вопрос')
      }
    }
  }

  for (const question of questions) {
    const options = ctx.optionsOf.get(question.id) ?? []
    const base = questionRow(question, ctx)
    if (question.displayCondition.trim()) {
      checkExpression(question.displayCondition, { question, base, place: 'Условие показа' }, new Set(), null)
    }
    const ownFieldKeys = SCHEMA_TYPES.has(question.answerType)
      ? new Set(options.map((option) => option.value.trim()).filter((key) => key))
      : new Set<string>()
    ;(ctx.effectsOf.get(question.id) ?? []).forEach((effect, index) => {
      const target = effectTarget(question, effect, ctx)
      // A fixed cost item of a repeater names no field, so it reads no `{attr:}` (as in the engine).
      const attrCategory: CategoryRef | null =
        target.kind === 'picked'
          ? { id: target.categoryId, name: target.category }
          : target.kind === 'picked-field' && !effect.fixedMaterial
            ? { id: target.field.materialCategoryId, name: target.field.materialCategory }
            : null
      checkExpression(effect.expression, { question, base, place: `Эффект №${index + 1}` }, ownFieldKeys, attrCategory)
    })
  }

  for (const hint of hints) {
    if (!hint.condition.trim()) continue
    const group = ctx.groupById.get(hint.groupId)
    checkExpression(
      hint.condition,
      {
        question: null,
        base: {
          subject: hint.text || '—',
          subjectNote: '',
          where: group ? `Подсказка группы «${group.name}»` : 'Группа удалена',
          link: hintLink(hint, ctx),
        },
        place: 'Условие подсказки',
      },
      new Set(),
      null,
    )
  }

  return issues
}

// ---- Rules: expressions, effects, links, structure -------------------------------------------

function checkSyntax(ctx: Context): Issue[] {
  const { issues, add } = collector()
  for (const question of ctx.questions) {
    const row = questionRow(question, ctx)
    if (question.displayCondition.trim()) {
      const error = validateLogic(question.displayCondition)
      if (error) {
        add({
          ...row,
          detail: 'Условие показа',
          kind: 'Условие показа',
          problem: `${error} — вопрос показывается всегда`,
        })
      }
    }
    ;(ctx.effectsOf.get(question.id) ?? []).forEach((effect, index) => {
      if (!effect.expression.trim()) return
      const error = validateLogic(effect.expression)
      if (error) {
        add({ ...row, detail: `Эффект №${index + 1}`, kind: 'Эффект', problem: `${error} — эффект всегда даёт 0` })
      }
    })
  }
  for (const hint of ctx.hints) {
    if (!hint.condition.trim()) continue
    const error = validateLogic(hint.condition)
    if (!error) continue
    const group = ctx.groupById.get(hint.groupId)
    add({
      subject: hint.text || '—',
      where: group ? `Подсказка группы «${group.name}»` : 'Группа удалена',
      detail: 'Условие подсказки',
      kind: 'Подсказка',
      problem: `${error} — подсказка не показывается никогда`,
      link: hintLink(hint, ctx),
    })
  }
  return issues
}

function checkEffects(ctx: Context): Issue[] {
  const { issues, add } = collector()
  for (const question of ctx.questions) {
    const row = questionRow(question, ctx)
    ;(ctx.effectsOf.get(question.id) ?? []).forEach((effect, index) => {
      const place = `Эффект №${index + 1}`
      if (!effect.expression.trim()) {
        add({ ...row, detail: place, kind: 'Пустое выражение', problem: 'Выражение пустое: эффект всегда даёт 0' })
      }
      const target = effectTarget(question, effect, ctx)
      // A fixed cost item names no field, so a stale key does not matter for it.
      if (target.kind === 'picked-field' && target.staleKey && !effect.fixedMaterial) {
        add({
          ...row,
          detail: `${place} · поле ${effect.targetFieldKey}`,
          kind: 'Поле цели не найдено',
          problem: `Поля «${effect.targetFieldKey}» больше нет — эффект применяется к первому полю «Выбор» («${target.field.label || target.field.value}»)`,
        })
      }
      // A fixed cost item added together with the picked material is checked like a fixed effect.
      if (target.kind !== 'fixed' && !effect.fixedMaterial) return
      if (!effect.materialId) {
        add({ ...row, detail: place, kind: 'Пункт не выбран', problem: 'Постоянный пункт не выбран: эффект ничего не добавляет в смету' })
        return
      }
      const material = ctx.materialById.get(effect.materialId)
      if (!material) {
        add({
          ...row,
          detail: `${place} · ${effect.materialId}`,
          kind: 'Пункт не найден',
          problem: 'Пункт удалён (или это чужой личный пункт): строка сметы без названия и цены',
        })
      } else if (material.isPrivate) {
        add({
          ...row,
          detail: `${place} · «${material.name}»`,
          kind: 'Личный пункт',
          problem: 'Пункт личный: другим пользователям он недоступен, строка сметы будет без названия и цены',
        })
      } else if (!material.isActive) {
        add({
          ...row,
          detail: `${place} · «${material.name}»`,
          kind: 'Пункт отключён',
          problem: 'Пункт отключён (неактивен), а эффект продолжает его добавлять',
        })
      }
    })
  }
  return issues
}

function checkLinks(ctx: Context): Issue[] {
  const { issues, add } = collector()
  for (const question of ctx.questions) {
    let kind = ''
    if (question.groupId && !ctx.groupById.has(question.groupId)) kind = 'Вопрос удалённой группы'
    else if (!question.groupId && !question.sectionTypeId) kind = 'Вопрос без раздела и группы'
    else if (!question.groupId && !ctx.sectionById.has(question.sectionTypeId)) kind = 'Вопрос удалённого раздела'
    if (!kind) continue
    add({
      subject: question.prompt || '—',
      subjectNote: question.factKey,
      where: ownerLabel(question, ctx),
      detail: question.groupId || question.sectionTypeId || '—',
      kind,
      problem: 'Вопрос не попадает ни в одно помещение и не виден в дереве правил',
      link: null,
    })
  }
  for (const section of ctx.sectionTypes) {
    if (!ctx.estateById.has(section.estateTypeId)) {
      add({
        subject: section.name,
        where: 'Тип раздела',
        detail: section.estateTypeId || '—',
        kind: 'Раздел удалённого типа объекта',
        problem: 'Тип объекта удалён: раздел не виден в дереве и в приложении',
        link: null,
      })
    }
    for (const groupId of section.groupIds) {
      if (ctx.groupById.has(groupId)) continue
      add({
        subject: section.name,
        where: 'Тип раздела',
        detail: groupId,
        kind: 'Подключена удалённая группа',
        problem: 'Раздел подключает группу, которой больше нет (на расчёт не влияет, но мусор)',
        link: sectionLink(section, ctx),
        fix: {
          kind: 'unlink-section-groups',
          sectionTypeId: section.id,
          groupIds: [groupId],
          label: 'Отвязать',
        },
      })
    }
  }
  for (const option of ctx.options) {
    if (ctx.questionById.has(option.questionId)) continue
    add({
      subject: option.label || option.value || '—',
      subjectNote: option.value,
      where: 'Вариант / поле',
      detail: option.questionId || '—',
      kind: 'Вариант без вопроса',
      problem: 'Вопрос удалён, вариант остался',
      link: null,
    })
  }
  for (const effect of ctx.effects) {
    if (ctx.questionById.has(effect.questionId)) continue
    add({
      subject: effect.expression || '—',
      where: 'Эффект',
      detail: effect.questionId || '—',
      kind: 'Эффект без вопроса',
      problem: 'Вопрос удалён, эффект остался',
      link: null,
    })
  }
  for (const hint of ctx.hints) {
    if (ctx.groupById.has(hint.groupId)) continue
    add({
      subject: hint.text || '—',
      where: 'Подсказка',
      detail: hint.groupId || '—',
      kind: 'Подсказка без группы',
      problem: 'Группа удалена, подсказка осталась',
      link: null,
    })
  }
  return issues
}

function checkStructure(ctx: Context): Issue[] {
  const { issues, add } = collector()
  for (const question of ctx.questions) {
    // A FACT exists only for its effects: without one it adds nothing anywhere.
    if (question.answerType === 'FACT' && (ctx.effectsOf.get(question.id) ?? []).length === 0) {
      add({
        ...questionRow(question, ctx),
        detail: ANSWER_TYPE_LABELS[question.answerType],
        kind: 'Факт без эффектов',
        problem: 'Пользователь факт не видит, а эффектов нет: он ничего не добавляет в смету',
      })
      continue
    }
    const count = (ctx.optionsOf.get(question.id) ?? []).length
    if (count > 0) continue
    if (question.answerType === 'SELECT' || question.answerType === 'KEY_SET') {
      add({
        ...questionRow(question, ctx),
        detail: ANSWER_TYPE_LABELS[question.answerType],
        kind: 'Нет вариантов',
        problem: 'Нет ни одного варианта: ответить нечем',
      })
    } else if (SCHEMA_TYPES.has(question.answerType)) {
      add({
        ...questionRow(question, ctx),
        detail: ANSWER_TYPE_LABELS[question.answerType],
        kind: 'Нет полей',
        problem: 'Нет ни одного поля: в опросе пустая форма',
      })
    }
  }
  const connected = new Set(ctx.sectionTypes.flatMap((section) => section.groupIds))
  const questionsOfGroup = groupBy(
    ctx.questions.filter((question) => question.groupId),
    (question) => question.groupId,
  )
  for (const group of ctx.groups) {
    const questionCount = questionsOfGroup.get(group.id)?.length ?? 0
    if (questionCount === 0) {
      add({
        subject: group.name,
        where: 'Группа вопросов',
        detail: group.isGlobal ? 'Глобальная' : 'По подключению',
        kind: 'Пустая группа',
        problem: 'В группе нет вопросов',
        link: links.group(group.id),
      })
    } else if (!group.isGlobal && !connected.has(group.id)) {
      add({
        subject: group.name,
        where: 'Группа вопросов',
        detail: `${questionCount} вопрос(ов)`,
        kind: 'Группа не подключена',
        problem: 'Группа не глобальная и не подключена ни к одному разделу: её вопросы нигде не задаются',
        link: links.group(group.id),
      })
    }
  }
  for (const section of ctx.sectionTypes) {
    if (!ctx.estateById.has(section.estateTypeId)) continue
    if (resolveSectionQuestions(section, section.id, ctx.questions, ctx.groups).length > 0) continue
    const estate = ctx.estateById.get(section.estateTypeId)
    add({
      subject: section.name,
      where: `${estate?.name ?? ''} › тип раздела`,
      detail: '—',
      kind: 'Раздел без вопросов',
      problem: 'В помещении такого раздела нет ни одного вопроса',
      link: sectionLink(section, ctx),
    })
  }
  for (const estate of ctx.estateTypes) {
    if (ctx.sectionTypes.some((section) => section.estateTypeId === estate.id)) continue
    add({
      subject: estate.name,
      where: 'Тип объекта',
      detail: '—',
      kind: 'Тип объекта без разделов',
      problem: 'Нет ни одного типа раздела: в проекте такого типа нечего добавить',
      link: links.estate(estate.id),
    })
  }
  for (const hint of ctx.hints) {
    if (hint.condition.trim()) continue
    const group = ctx.groupById.get(hint.groupId)
    add({
      subject: hint.text || '—',
      where: group ? `Подсказка группы «${group.name}»` : 'Группа удалена',
      detail: '—',
      kind: 'Подсказка без условия',
      problem: 'Пустое условие: подсказка не показывается никогда',
      link: hintLink(hint, ctx),
    })
  }
  return issues
}

// ---- Translations ----------------------------------------------------------------------------

function checkTranslations(ctx: Context): Issue[] {
  const { issues, add } = collector()
  const missingIn = (base: string, i18n: Record<string, string>) =>
    base.trim()
      ? TRANSLATION_LANGUAGES.filter((language) => !(i18n[language.tag] ?? '').trim()).map(
          (language) => language.tag.toUpperCase(),
        )
      : []
  const check = (
    kind: string,
    subject: string,
    i18n: Record<string, string>,
    link: string | null,
    subjectNote = '',
  ) => {
    const missing = missingIn(subject, i18n)
    if (missing.length === 0) return
    add({ subject, subjectNote, where: kind, detail: missing.join(', '), kind, problem: `Нет перевода: ${missing.join(', ')}`, link })
  }
  for (const estate of ctx.estateTypes) check('Тип объекта', estate.name, estate.nameI18n, links.estate(estate.id))
  for (const section of ctx.sectionTypes) {
    check('Тип раздела', section.name, section.nameI18n, sectionLink(section, ctx))
  }
  for (const group of ctx.groups) check('Группа вопросов', group.name, group.nameI18n, links.group(group.id))
  for (const question of ctx.questions) {
    // A FACT's text is never shown to the user, so a missing translation does not matter.
    if (question.answerType === 'FACT') continue
    check('Вопрос', question.prompt, question.promptI18n, questionLink(question, ctx), question.factKey)
  }
  for (const option of ctx.options) {
    const question = ctx.questionById.get(option.questionId)
    if (!question) continue
    check(
      'Вариант / поле',
      option.label,
      option.labelI18n,
      questionLink(question, ctx),
      `${question.prompt || question.factKey} · ${option.value}`,
    )
  }
  for (const hint of ctx.hints) check('Подсказка', hint.text, hint.textI18n, hintLink(hint, ctx))
  for (const category of ctx.categories) {
    check('Категория', category.name, category.nameI18n, links.category(category.id))
  }
  for (const unit of ctx.units) check('Единица', unit.name, unit.nameI18n, links.unit(unit.id), unit.symbol)
  for (const material of [...ctx.materials].sort(byName)) {
    if (material.isPrivate) continue
    check('Пункт затрат', material.name, material.nameI18n, links.material(material.id), material.code)
  }
  return issues
}
